import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityGithubInsightsContent,
  EntityGithubInsightsLanguagesCard,
  EntityGithubInsightsReadmeCard,
  EntityGithubInsightsReleasesCard,
  isGithubInsightsAvailable,
  EntityGithubInsightsContributorsCard,
} from '@roadiehq/backstage-plugin-github-insights';
import {
  EntityGithubPullRequestsContent,
  EntityGithubPullRequestsOverviewCard,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import {
  isNewRelicDashboardAvailable,
  EntityNewRelicDashboardContent,
  EntityNewRelicDashboardCard,
} from '@backstage-community/plugin-newrelic-dashboard';
import { EntityOpsgenieOnCallListCard, isOpsgenieOnCallListAvailable } from '@moveinc/backstage-plugin-opsgenie';
import { EntityArgoCDContent, EntityArgoCDOverviewCard, isArgocdAvailable } from '@roadiehq/backstage-plugin-argo-cd';

import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { EntityUserProfileCard, EntityGroupProfileCard, EntityOwnershipCard } from '@backstage/plugin-org';
import { EntityCircleCIContent, isCircleCIAvailable } from '@moveinc/plugin-circleci';
import { EntityTechdocsContent, isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import { ALL_RELATION_PAIRS, Direction, EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  CUSTOM_RELATION_PAIRS,
  RELATION_SCAFFOLDED_BY,
  RELATION_SCAFFOLDER_OF,
} from '@developer-portal/plugin-catalog-common';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { EntityJiraDashboardContent, isJiraDashboardAvailable } from '@axis-backstage/plugin-jira-dashboard';
import { EntityAdHocJobContent } from '../adhoc-jobs/AdHocJobEmbeddedWorkflow';
import { SentinelSecretsManagerContent } from '@developer-portal/backstage-plugin-sentinel-secrets-manager';
import { GleanTechDocsSearch } from '@moveinc/backstage-plugin-glean';
import { FeatureFlagged } from '@backstage/core-app-api';
import { EntityScorecardsPage, EntityTasksPage } from '@get-dx/backstage-plugin';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CriticalityCard, hasCriticality } from '../criticality-card/CriticalityCard';
import { GroupRolesCard } from '../group-roles-card/GroupRolesCard';
import { EntityKubernetesContent, isKubernetesAvailable } from '@backstage/plugin-kubernetes';

const EntityScorecardsPageWrapped = () => {
  const { entity } = useEntity();
  return <EntityScorecardsPage entityIdentifier={entity.metadata.name} />;
};

const EntityTasksPageWrapped = () => {
  const { entity } = useEntity();
  return <EntityTasksPage entityIdentifier={entity.metadata.name} />;
};
const relationPairs = [...ALL_RELATION_PAIRS, ...CUSTOM_RELATION_PAIRS];

const techdocsContent = (
  <EntityTechdocsContent withSearch={false}>
    <TechDocsAddons>
      <GleanTechDocsSearch />
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const customEntityFilterKind = ['Component', 'API', 'System'];
const customEntityFilterGraphKind = ['Group', 'System', 'User', 'Component'];

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isCircleCIAvailable}>
      <EntityCircleCIContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CircleCI available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CircleCI for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations/#circlecicomproject-slug"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const jiraContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={entity => isJiraDashboardAvailable(entity)}>
      <EntityJiraDashboardContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No Jira Inoformation available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable Jira for it. You can read more about the required annotations for this plugin by clicking the JIRA ANNOTATIONS button below."
        action={
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                href="https://developer-portal.rdc.moveaws.com/docs/default/component/developer-portal-web/plugins/jira/component-configuration/"
              >
                Jira Annotations
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                href="https://github.com/MoveRDC/typescript-starter-app/blob/1a85aae1e811cfb354a1a74e08815e43fb271628/catalog-info.yaml#L14-L16"
              >
                Example
              </Button>
            </Grid>
          </Grid>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCriticality}>
        <Grid item md={6}>
          <CriticalityCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isOpsgenieOnCallListAvailable}>
        <Grid item md={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityOpsgenieOnCallListCard title="OpsGenie Who Is On-Call" variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard
        height={400}
        relationPairs={relationPairs}
        maxDepth={1}
        kinds={customEntityFilterGraphKind}
      />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={isKind('component')}>
        <Grid item md={6} xs={12}>
          <EntitySonarQubeCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
        <Grid item md={6}>
          <EntityArgoCDOverviewCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item md={6}>
      <EntityGithubPullRequestsOverviewCard variant="gridItem" />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={isGithubInsightsAvailable}>
        <Grid container item>
          <Grid item md={6}>
            <EntityGithubInsightsLanguagesCard />
            <EntityGithubInsightsReleasesCard />
            <EntityGithubInsightsContributorsCard />
          </Grid>
          <Grid item md={6}>
            <EntityGithubInsightsReadmeCard maxHeight={400} />
          </Grid>
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isNewRelicDashboardAvailable}>
        <Grid item md={6} xs={12}>
          <EntityNewRelicDashboardCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <Grid item xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>
  </Grid>
);

const apiContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityProvidedApisCard />
    </Grid>
    <Grid item md={6}>
      <EntityConsumedApisCard />
    </Grid>
  </Grid>
);

const dependenciesContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityDependsOnComponentsCard />
    </Grid>
    <Grid item md={6}>
      <EntityDependsOnResourcesCard />
    </Grid>
  </Grid>
);

const adHocContent = (
  <EntityLayout.Route path="/ad-hoc-jobs" title="Ad-Hoc Jobs">
    <EntityAdHocJobContent />
  </EntityLayout.Route>
);

const sentinelSecretsContent = <SentinelSecretsManagerContent />;

type ComponentContentOptions = {
  showApiContent?: boolean;
  showArgoCdContent?: boolean;
};
const componentContent = (options?: ComponentContentOptions) => {
  const { showApiContent = true, showArgoCdContent = true } = options ?? {};

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/ci-cd" title="CircleCI">
        {cicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/jira" title="Jira">
        {jiraContent}
      </EntityLayout.Route>

      {showArgoCdContent && (
        <EntityLayout.Route path="/argocd" title="Argo CD">
          <EntityArgoCDContent />
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/pull-requests" title="Pull Requests">
        <EntityGithubPullRequestsContent />
      </EntityLayout.Route>

      {showApiContent && (
        <EntityLayout.Route path="/api" title="API">
          {apiContent}
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        {dependenciesContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/code-insights" title="Code Insights">
        <EntityGithubInsightsContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/newrelic-dashboard" title="New Relic Dashboard" if={isNewRelicDashboardAvailable}>
        <EntityNewRelicDashboardContent />
      </EntityLayout.Route>

      {adHocContent}

      <EntityLayout.Route path="/secrets" title="Secrets">
        {sentinelSecretsContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/kubernetes" title="Kubernetes">
        <EntitySwitch>
          <EntitySwitch.Case if={isKubernetesAvailable}>
            <EntityKubernetesContent refreshIntervalMs={30000} />
          </EntitySwitch.Case>
          <EntitySwitch.Case>
            <EmptyState
              title="No pods detected"
              missing="data"
              description="This component has no Kubernetes resources associated with it. To enable pod visibility, add the backstage.io/kubernetes-id annotation to your catalog-info.yaml."
            />
          </EntitySwitch.Case>
        </EntitySwitch>
      </EntityLayout.Route>

      <FeatureFlagged with="show-get-dx">
        <EntityLayout.Route path="/dx-scorecards" title="DX" if={isComponentType('service')}>
          <EntityScorecardsPageWrapped />
        </EntityLayout.Route>
      </FeatureFlagged>

      <FeatureFlagged with="show-get-dx">
        <EntityLayout.Route path="/dx-tasks" title="DX Tasks" if={isComponentType('service')}>
          <EntityTasksPageWrapped />
        </EntityLayout.Route>
      </FeatureFlagged>
    </EntityLayout>
  );
};

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (showAdHocJobs: boolean = false, showSecretsTab: boolean = false) => (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    {showAdHocJobs && adHocContent}

    {showSecretsTab && (
      <EntityLayout.Route path="/secrets" title="Secrets">
        {sentinelSecretsContent}
      </EntityLayout.Route>
    )}
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType(['service', 'library'])}>{componentContent()}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('mobile-app')}>
      {componentContent({ showArgoCdContent: false })}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('website')}>{componentContent({ showApiContent: false })}</EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage(true, true)}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>

        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} relationPairs={relationPairs} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard entityFilterKind={customEntityFilterKind} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard entityFilterKind={customEntityFilterKind} />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupRolesCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityOpsgenieOnCallListCard title="OpsGenie Who Is On-Call" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs" if={isTechDocsAvailable}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>

        <EntitySwitch>
          <EntitySwitch.Case if={hasCriticality}>
            <Grid item md={6}>
              <CriticalityCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} relationPairs={relationPairs} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
          RELATION_SCAFFOLDED_BY,
          RELATION_SCAFFOLDER_OF,
        ]}
        relationPairs={relationPairs}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>

        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} relationPairs={relationPairs} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage()}</EntitySwitch.Case>
  </EntitySwitch>
);
